import type { Preferences, PreferencesResponse } from '@fruuit/shared'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { api } from '../../api/client'
import { useApiMutation, useApiQuery } from '../../api/hooks'

/**
 * The signed-in user's preferences (I-06). They live on the server, never in
 * localStorage (GAP-DD-022), and only pick screen defaults: a failed load or
 * save falls back to the screen's own default and is never shown as an error.
 */
export const PREFERENCES_KEY = ['preferences'] as const

export function usePreferences() {
  return useApiQuery<PreferencesResponse>(PREFERENCES_KEY, '/preferences', {
    staleTime: 5 * 60_000,
    retry: false,
  })
}

/** Returns `save(patch)`: merges the patch into the stored preferences (CAS on revision). */
export function useSavePreferences() {
  const queryClient = useQueryClient()
  const current = usePreferences().data
  const mutation = useApiMutation(
    (vars: { revision: number; preferences: Preferences }, { requestId }) =>
      api.put<PreferencesResponse>('/preferences', vars, { requestId }),
    {
      onSuccess: (data) => queryClient.setQueryData(PREFERENCES_KEY, data),
      // A stale revision (another tab saved first): reload and let the next change win.
      onError: () => queryClient.invalidateQueries({ queryKey: PREFERENCES_KEY }),
    },
  )
  const { mutateAsync } = mutation
  const save = useCallback(
    (patch: Preferences): Promise<PreferencesResponse | null> => {
      if (!current) return Promise.resolve(null)
      return mutateAsync({
        revision: current.revision,
        preferences: { ...current.preferences, ...patch },
      }).catch(() => null)
    },
    [current, mutateAsync],
  )
  return { save, saving: mutation.isPending }
}
